/** Blind deck item shape as shipped in /data/deck.json (docs/ARCHITECTURE.md §4.1, §4.4). */
export interface PublicDeckItem {
  policy_id: string;
  content_version: number;
  text: string;
  detail: string;
  weight: number;
  category: string;
  axis_a_loading: number;
  axis_b_loading: number;
}

export type SupportValue = -2 | -1 | 0 | 1 | 2;

/** Matches packages/scoring's Rating shape and SubmitRequestBody's ratings[] entries. */
export interface RatingPayload {
  p: string;
  v: SupportValue;
  d: number;
  i: number;
}

/** voted_2020/voted_2024 hold *who* the rater voted for (DEMOGRAPHIC_OPTIONS
 * candidate-choice values, e.g. "biden"/"trump"/"other"/"didnt_vote"), not
 * whether they voted — repurposed from the original yes/no/not_eligible
 * values without a D1 migration, since both D1 columns and this wire field
 * were always plain, unconstrained TEXT (see memory.md). */
export interface Demographics {
  age_range: string | null;
  us_state: string | null;
  party_affiliation: string | null;
  voted_2020: string | null;
  voted_2024: string | null;
}

export const DEMOGRAPHIC_FIELDS: (keyof Demographics)[] = [
  "age_range",
  "us_state",
  "party_affiliation",
  "voted_2020",
  "voted_2024",
];

export type ReferrerClass = "reddit" | "x" | "facebook" | "bluesky" | "search" | "direct" | "internal" | "other";

export type Phase = "intro" | "rating" | "rate_more" | "demographics" | "submitting" | "reveal" | "error";

/**
 * Mirrors packages/worker/src/types.ts SubmitResponseBody. Duplicated rather
 * than imported across the workspace boundary (the worker package has no
 * public "types" export path) — and per memory.md's Phase 2 note, this wire
 * contract is a draft the owner hasn't reviewed yet, so keep it easy to
 * diverge/reconcile rather than sharing a hard type dependency.
 */
export interface RevealItem {
  policy_id: string;
  source_label: string;
  administration: string;
  party: string;
  citation_url: string;
  context: string;
}

export interface SubmitResponseBody {
  id: string;
  persisted: boolean;
  share_token: string | null;
  deck_version: string;
  archetype: string;
  axis_a: number;
  axis_b: number;
  axis_a_undetermined: boolean;
  axis_b_undetermined: boolean;
  cross_party_pct: number | null;
  n_rated: number;
  reveal: RevealItem[];
}

export interface SessionState {
  deck: PublicDeckItem[];
  /** Cards the visitor has agreed to rate so far; `deck` is the whole shuffled deck. */
  cap: number;
  phase: Phase;
  currentIndex: number;
  ratings: RatingPayload[];
  cardShownAt: number;
  interactionEvents: number;
  sessionStartedAt: number;
  demographics: Demographics;
  error: string | null;
}
